import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Select, Checkbox } from '../atoms';
import { DataTable, StatCard, StatCardRow, useToast, NewInformationRequestModal } from '../organisms';
import { useEntityPermission } from '../lib/usePermissions';
import { formatDate } from '../lib/orgTimezone';

/**
 * InformationRequests — /information-requests
 *
 * The shared status board for every client information request: who requested
 * what, sent / received / overdue, and how many items are still outstanding —
 * so nobody forwards an email to a colleague to learn a status.
 *
 * Reads GET /api/information-requests/board (contract point 03-2) for the live
 * board rows; loading / empty / error states are honest (no placeholder data).
 * The Send action calls the real POST /api/information-requests/:id/send endpoint
 * (marks the draft sent + emails the contact via the Resend keystone) and reports
 * the real success/failure.
 *
 * Domain 03.1 — RBAC, Identity & Org Settings.
 */

type RequestStatus = 'draft' | 'sent' | 'partial' | 'complete';

interface BoardRow {
  id: string;
  title: string;
  status: RequestStatus;
  tax_year: number;
  property_name: string;
  client_name: string;
  due_date: string | null;
  is_overdue: boolean;
  items_total: number;
  items_received: number;
  items_outstanding: number;
}

const statusVariant: Record<RequestStatus, 'neutral' | 'info' | 'warning' | 'success'> = {
  draft: 'neutral', sent: 'info', partial: 'warning', complete: 'success',
};
const statusLabel: Record<RequestStatus, string> = {
  draft: 'Draft', sent: 'Sent', partial: 'Partial', complete: 'Complete',
};

export function InformationRequests() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState('');
  const [taxYear, setTaxYear] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [searchParams] = useSearchParams();
  const clientFilter = searchParams.get('client_id');

  const canView = useEntityPermission('information_request', 'can_view');
  const canSend = useEntityPermission('information_request', 'can_edit');
  const canCreate = useEntityPermission('information_request', 'can_add');

  const boardQuery = useQuery<BoardRow[]>({
    queryKey: ['information-requests-board', clientFilter],
    queryFn: ({ signal }) => api.get<BoardRow[]>(`/api/information-requests/board${clientFilter ? `?client_id=${encodeURIComponent(clientFilter)}` : ''}`, signal),
    enabled: canView,
  });

  const send = useMutation({
    mutationFn: (row: BoardRow) => api.post(`/api/information-requests/${row.id}/send`),
    onSuccess: (_d, row) => {
      queryClient.invalidateQueries({ queryKey: ['information-requests-board'] });
      toast({ variant: 'success', message: `Request "${row.title}" sent.` });
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : 'Could not send the request.';
      toast({ variant: 'error', message: msg });
    },
  });

  if (!canView) {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>Access denied</h2>
        <p style={{ margin: 0 }}>You do not have permission to view information requests.</p>
      </div>
    );
  }

  const allRows = boardQuery.data ?? [];

  const years = Array.from(new Set(allRows.map(r => r.tax_year))).sort((a, b) => b - a);
  const rows = allRows.filter(r =>
    (!status || r.status === status) &&
    (!taxYear || String(r.tax_year) === taxYear) &&
    (!overdueOnly || r.is_overdue),
  );

  const outstanding = rows.reduce((sum, r) => sum + r.items_outstanding, 0);
  const overdueCount = rows.filter(r => r.is_overdue).length;
  const hasFilters = !!(status || taxYear || overdueOnly);

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--sp-4)', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: 'var(--fs-3xl)',
            fontWeight: 'var(--fw-semibold)',
            letterSpacing: 'var(--ls-tight)',
            color: 'var(--color-ink)',
          }}>Information requests</h1>
          <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
            Every client request in one place — sent, received, and what is still outstanding.
          </p>
        </div>
        {canCreate && (
          <Button variant="primary" onClick={() => setShowNew(true)}>New request</Button>
        )}
      </header>

      {clientFilter && (
        <div style={{ marginBottom: 'var(--sp-4)', padding: 'var(--sp-2) var(--sp-3)', background: 'var(--brand-primary-bg)', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)', display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          <span>Showing requests for <strong>{allRows[0]?.client_name ?? 'this client'}</strong></span>
          <a href="/information-requests" onClick={(e) => { e.preventDefault(); navigate('/information-requests'); }} style={{ color: 'var(--brand-primary)', cursor: 'pointer', fontWeight: 'var(--fw-medium)' }}>Clear filter</a>
        </div>
      )}

      <div style={{ marginBottom: 'var(--sp-6)' }}>
        <StatCardRow>
          <StatCard label="Requests" value={String(rows.length)} caption={hasFilters ? 'in current filter' : 'in scope'} accent="hunter" />
          <StatCard label="Items outstanding" value={String(outstanding)} caption="not yet received" accent="info" />
          <StatCard label="Overdue" value={String(overdueCount)} caption={overdueCount > 0 ? 'past due date' : 'all on track'} accent={overdueCount > 0 ? 'amber' : 'green'} />
        </StatCardRow>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', alignItems: 'flex-end', marginBottom: 'var(--sp-5)' }}>
        <FilterField label="Status">
          <Select
            preserveOrder
            options={[{ value: '', label: 'All statuses' }, ...(['draft', 'sent', 'partial', 'complete'] as RequestStatus[]).map(s => ({ value: s, label: statusLabel[s] }))]}
            value={status}
            onChange={e => setStatus(e.target.value)}
          />
        </FilterField>
        <FilterField label="Tax year">
          <Select
            preserveOrder
            options={[{ value: '', label: 'All years' }, ...years.map(y => ({ value: String(y), label: String(y) }))]}
            value={taxYear}
            onChange={e => setTaxYear(e.target.value)}
          />
        </FilterField>
        <div style={{ paddingBottom: 'var(--sp-2)' }}>
          <Checkbox label="Overdue only" checked={overdueOnly} onChange={e => setOverdueOnly((e.target as HTMLInputElement).checked)} />
        </div>
      </div>

      <BoardTable
        rows={rows}
        loading={boardQuery.isLoading}
        error={boardQuery.error as Error | null}
        canSend={canSend}
        sending={send.isPending}
        onSend={(row) => send.mutate(row)}
        onRowClick={(row) => navigate(`/information-requests/${row.id}`)}
      />

      <NewInformationRequestModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ['information-requests-board'] })}
      />
    </>
  );
}

function BoardTable({
  rows, loading, error, canSend, sending, onSend, onRowClick,
}: {
  rows: BoardRow[];
  loading: boolean;
  error: Error | null;
  canSend: boolean;
  sending: boolean;
  onSend: (row: BoardRow) => void;
  onRowClick: (row: BoardRow) => void;
}) {
  const columns = useMemo<ColumnDef<BoardRow>[]>(() => [
    { accessorKey: 'title', header: 'Request' },
    { accessorKey: 'client_name', header: 'Client' },
    { accessorKey: 'property_name', header: 'Property' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => { const s = getValue() as RequestStatus; return <Badge variant={statusVariant[s] ?? 'neutral'}>{statusLabel[s] ?? String(s ?? '—').replace(/_/g, ' ')}</Badge>; },
    },
    { accessorKey: 'tax_year', header: 'Year' },
    {
      id: 'items',
      header: 'Items',
      cell: ({ row }) => `${row.original.items_received} / ${row.original.items_total}`,
    },
    {
      accessorKey: 'due_date',
      header: 'Due',
      cell: ({ row }) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          {formatDate(row.original.due_date, { dateOnly: true })}
          {row.original.is_overdue && <Badge variant="warning" dot>Overdue</Badge>}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        canSend && row.original.status === 'draft'
          ? <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                size="sm"
                disabled={sending}
                onClick={(e) => { e.stopPropagation(); onSend(row.original); }}
              >Send</Button>
            </div>
          : null
      ),
    },
  ], [canSend, sending, onSend]);

  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading requests…</p>;
  if (error) {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        Failed to load information requests. {error.message}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div style={{
        padding: 'var(--sp-6)',
        background: 'var(--color-card)',
        border: '1px dashed var(--color-border)',
        borderRadius: 'var(--r-lg)',
        color: 'var(--color-muted)',
        textAlign: 'center',
        fontSize: 'var(--fs-md)',
      }}>No information requests match these filters.</div>
    );
  }
  return <DataTable data={rows} columns={columns} onRowClick={onRowClick} />;
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 180 }}>
      <span style={{
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>{label}</span>
      {children}
    </label>
  );
}
